/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal */

if (!customElements.get('pop-up')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class PopUp extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;
        this.subscribedCookie = `${this.id}-subscribed`;
        this.currentStep = 'teaser';
        this.widget = document.querySelector(`[data-popup-id="${this.id}"]`);

        // Set up step navigation
        this.setupStepNavigation();

        // Set up widget functionality
        this.setupWidget();

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else if (!getCookie(this.cookie) && (this.dataset.showOnMobile === 'true'
          || theme.mediaMatches.md)) {
          if (this.querySelector('.alert')) {
            this.open();
          } else if (this.dataset.trigger === 'delay') {
            setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
          } else if (this.dataset.trigger === 'exit' && theme.mediaMatches.md) {
            this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
            document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
          }
        } else if (getCookie(this.cookie) && !getCookie(this.subscribedCookie)) {
          // Show widget if popup was dismissed but user didn't subscribe
          this.showWidget();
        }
      }

      /**
       * Sets up the side widget functionality
       */
      setupWidget() {
        if (!this.widget) return;

        const widgetButton = this.widget.querySelector('.js-popup-widget-trigger');
        if (widgetButton) {
          widgetButton.addEventListener('click', (evt) => {
            evt.preventDefault();
            this.hideWidget();
            this.open();
          });
        }
      }

      /**
       * Shows the side widget with animation
       */
      showWidget() {
        if (!this.widget) return;

        setTimeout(() => {
          this.widget.style.display = 'block';
          requestAnimationFrame(() => {
            this.widget.classList.add('is-visible');
          });
        }, 1000); // Delay before showing widget
      }

      /**
       * Hides the side widget
       */
      hideWidget() {
        if (!this.widget) return;

        this.widget.classList.remove('is-visible');
        setTimeout(() => {
          this.widget.style.display = 'none';
        }, 300);
      }

      /**
       * Sets up step navigation for the popup
       */
      setupStepNavigation() {
        const nextStepButtons = this.querySelectorAll('.js-popup-next-step');
        
        nextStepButtons.forEach(button => {
          button.addEventListener('click', (evt) => {
            evt.preventDefault();
            this.goToNextStep();
          });
        });

        // Check if teaser exists
        this.teaserStep = this.querySelector('[data-step="teaser"]');
        this.contentStep = this.querySelector('[data-step="content"]');
        
        // If no teaser, show content immediately
        if (!this.teaserStep && this.contentStep) {
          this.contentStep.style.display = 'block';
        }

        // Listen for newsletter form submissions
        const newsletterForm = this.querySelector('form[action*="/contact"]');
        if (newsletterForm) {
          newsletterForm.addEventListener('submit', () => {
            // Mark as subscribed when form is submitted
            setTimeout(() => {
              this.markAsSubscribed();
            }, 500);
          });
        }
      }

      /**
       * Navigate to the next step (from teaser to content)
       */
      goToNextStep() {
        if (this.teaserStep && this.contentStep) {
          // Fade out teaser
          this.teaserStep.style.opacity = '0';
          this.teaserStep.style.transition = 'opacity 0.3s ease';
          
          setTimeout(() => {
            this.teaserStep.style.display = 'none';
            this.contentStep.style.display = 'block';
            this.contentStep.style.opacity = '0';
            
            // Fade in content
            requestAnimationFrame(() => {
              this.contentStep.style.transition = 'opacity 0.3s ease';
              this.contentStep.style.opacity = '1';
            });
            
            this.currentStep = 'content';
          }, 300);
        }
      }

      /**
       * Opens the popup and resets to first step
       */
      open(opener) {
        // Reset to teaser step if it exists
        if (this.teaserStep && this.contentStep) {
          this.teaserStep.style.display = 'block';
          this.teaserStep.style.opacity = '1';
          this.contentStep.style.display = 'none';
          this.currentStep = 'teaser';
        }
        
        super.open(opener);
      }

      /**
       * Triggers when the web component is removed from the DOM
       */
      disconnectedCallback() {
        if (this.mouseLeaveHandler) {
          document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        }
      }

      /**
       * Handles 'mouseleave' events on the body.
       * @param {object} evt - Event object.
       */
      handleMouseLeave(evt) {
        if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'close' events on the modal.
       */
      close() {
        super.close();
        setCookie(this.cookie, true, parseInt(this.dataset.dismissDays, 10));
        
        // Show widget if user dismissed without subscribing
        if (!getCookie(this.subscribedCookie)) {
          this.showWidget();
        }
      }

      /**
       * Marks the user as subscribed (called when form is submitted)
       */
      markAsSubscribed() {
        setCookie(this.subscribedCookie, true, 365); // Store for 1 year
        this.hideWidget();
      }
    }

    customElements.define('pop-up', PopUp);
  });
}
